import { useEffect, useRef, useState } from "react";
import { principalSchema, type Principal } from "@proof/core";
import { ApiRepository, ProofApiError } from "@proof/sdk";
import { App } from "./App";
import {
  createWorkspaceTransport,
  type WorkspaceTransport,
} from "./workspace-transport";
import { Button, Control } from "./components";
import { operatorNavigate } from "./OperatorAccess";

type Access =
  | { kind: "checking" }
  | { kind: "connect"; message?: string }
  | { kind: "unavailable" }
  | {
      kind: "ready";
      transport: WorkspaceTransport;
      repository: ApiRepository;
      key: string;
    };
const identityKey = (principal: Principal) =>
  JSON.stringify([
    principal.tenantId,
    principal.subject,
    principal.revision,
    principal.authorization,
    principal.operatorContext,
  ]);
export function TenantWorkspace({
  developmentIdentity = false,
  operatorContextId,
  onOperatorAccessLost,
  onOperatorExit,
  accountAccess,
  registerBeforeLeave,
}: {
  developmentIdentity?: boolean;
  operatorContextId?: string;
  onOperatorAccessLost?: () => void;
  onOperatorExit?: () => void;
  accountAccess?: { onLeave: () => void; onExpired: () => void; name?: string };
  registerBeforeLeave?: (handler: () => Promise<void>) => () => void;
}) {
  const [credentials, setCredentials] = useState<{
      token?: string;
      attempt: number;
      connect: boolean;
      message?: string;
    }>({ attempt: 0, connect: true }),
    [entry, setEntry] = useState(""),
    [entryError, setEntryError] = useState(""),
    [access, setAccess] = useState<Access>({ kind: "checking" });
  const refresh = useRef<() => void>(() => {});
  useEffect(() => {
    if (!credentials.connect) {
      setAccess({
        kind: "connect",
        message: credentials.message || "Workspace disconnected.",
      });
      return;
    }
    let current = true,
      running = false,
      scope: WorkspaceTransport | undefined,
      key = "";
    const lost = () => {
      scope?.close();
      if (current) {
        if (accountAccess) {
          setAccess({ kind: "checking" });
          accountAccess.onExpired();
          return;
        }
        if (operatorContextId) {
          setAccess({ kind: "checking" });
          onOperatorAccessLost?.();
          return;
        }
        setEntry("");
        setAccess({
          kind: "connect",
          message:
            "Workspace access expired or changed. Reconnect to continue.",
        });
        setCredentials((old) => ({
          attempt: old.attempt + 1,
          connect: false,
          message:
            "Workspace access could not be verified. Reconnect to continue.",
        }));
      }
    };
    const initial = createWorkspaceTransport({
      demo: false,
      token: credentials.token,
      onAccessLost: lost,
    });
    setAccess({ kind: "checking" });
    const check = async () => {
      if (running || !current) return;
      running = true;
      try {
        const principal = principalSchema.parse(
          await (scope || initial).api("/me"),
        );
        if (!principal.authorization) throw new Error("Missing authorization");
        if (
          operatorContextId &&
          principal.operatorContext?.id !== operatorContextId
        )
          throw new ProofApiError(
            "Operator context changed",
            401,
            "IMPERSONATION_REQUIRED",
          );
        if (!current) return;
        const next = identityKey(principal);
        if (!scope || next !== key) {
          scope?.close();
          scope = createWorkspaceTransport({
            demo: false,
            principal,
            token: credentials.token,
            onAccessLost: lost,
          });
          key = next;
          setAccess({
            kind: "ready",
            transport: scope,
            repository: new ApiRepository(principal.tenantId, scope.client),
            key: `${credentials.attempt}:${key}`,
          });
        }
      } catch (error) {
        scope?.close();
        scope = undefined;
        if (current) {
          if (
            error instanceof ProofApiError &&
            [401, 403].includes(error.status)
          )
            lost();
          else setAccess({ kind: "unavailable" });
        }
      } finally {
        running = false;
      }
    };
    refresh.current = () => {
      void check();
    };
    void check();
    const timer = setInterval(() => void check(), 5000);
    const focus = () => void check();
    window.addEventListener("focus", focus);
    return () => {
      current = false;
      initial.close();
      scope?.close();
      clearInterval(timer);
      window.removeEventListener("focus", focus);
      refresh.current = () => {};
    };
  }, [credentials, operatorContextId, onOperatorAccessLost]);
  if (access.kind === "ready")
    return (
      <App
        key={access.key}
        workspace={{
          repository: access.repository,
          transport: access.transport,
          onLeave: () => {
            access.transport.close();
            if (accountAccess) {
              accountAccess.onLeave();
              return;
            }
            if (operatorContextId) {
              onOperatorExit?.();
              return;
            }
            setCredentials((old) => ({
              attempt: old.attempt + 1,
              connect: false,
            }));
          },
          onIdentityChanged: () => refresh.current(),
          registerBeforeLeave,
          displayName: accountAccess?.name,
        }}
      />
    );
  return (
    <main className="instance-shell instance-access">
      <header className="instance-brand">
        Proof Studio <span>Tenant workspace</span>
      </header>
      <section className="instance-panel">
        {access.kind === "checking" ? (
          <h1 role="status">Checking workspace access…</h1>
        ) : access.kind === "unavailable" ? (
          <>
            <h1>Workspace unavailable</h1>
            <p>
              Access could not be verified. Reconnect after the API or host
              identity service recovers.
            </p>
            <Button
              onClick={() =>
                setCredentials((old) => ({ ...old, attempt: old.attempt + 1 }))
              }
            >
              Retry workspace access
            </Button>
          </>
        ) : (
          <>
            <h1>Connect your workspace</h1>
            <p>
              {access.message ||
                "Use access supplied by your host identity provider."}
            </p>
            {!developmentIdentity && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (
                    !entry ||
                    entry.length > 4096 ||
                    /[^\x21-\x7e]/.test(entry)
                  ) {
                    setEntryError(
                      "Enter only the access token value, without a Bearer prefix or whitespace.",
                    );
                    return;
                  }
                  setEntryError("");
                  setCredentials((old) => ({
                    token: entry,
                    attempt: old.attempt + 1,
                    connect: true,
                  }));
                  setEntry("");
                }}
              >
                <Control label="Host access token">
                  <input
                    type="password"
                    autoComplete="off"
                    value={entry}
                    maxLength={4096}
                    required
                    onChange={(event) => {
                      setEntry(event.target.value);
                      setEntryError("");
                    }}
                  />
                </Control>
                {entryError && <p role="alert">{entryError}</p>}
                <p>
                  Access tokens and workspace form values stay in memory.
                  Reloading requires host access again.
                </p>
                <Button type="submit" kind="primary">
                  Connect workspace
                </Button>
              </form>
            )}
            {developmentIdentity && (
              <Button
                onClick={() =>
                  setCredentials((old) => ({
                    attempt: old.attempt + 1,
                    connect: true,
                  }))
                }
              >
                Reconnect workspace
              </Button>
            )}
          </>
        )}
        <Button onClick={() => operatorNavigate("/operator/login")}>
          Operator sign in
        </Button>
      </section>
    </main>
  );
}
